import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { membershipApi } from "./api";
import type {
  MemberStatus,
  MembershipSource,
  MembershipWorkflowStage,
  PaymentStatus,
  ReportMemberStatus,
} from "./types";

export function useMembershipStats() {
  return useQuery({
    queryKey: ["membership", "stats"],
    queryFn: membershipApi.getStats,
    staleTime: 1000 * 60 * 3,
  });
}

export function useMembershipCategories() {
  return useQuery({
    queryKey: ["membership", "categories"],
    queryFn: membershipApi.listCategories,
  });
}

export function useMembershipFeeTypes() {
  return useQuery({
    queryKey: ["membership", "fee-types"],
    queryFn: membershipApi.listFeeTypes,
  });
}

export function useMembershipApplications(params: {
  page: number;
  size: number;
  search?: string;
  status?: MemberStatus | "";
  source?: MembershipSource | "";
  converted?: boolean;
}) {
  return useQuery({
    queryKey: ["membership", "applications", params],
    queryFn: () => membershipApi.listApplications(params),
  });
}

export function useMembers(params: {
  page: number;
  size: number;
  search?: string;
  status?: MemberStatus | "";
  categoryId?: number | "";
  workflowStage?: MembershipWorkflowStage | "";
  /** Filter by the year the member was activated — backend to support */
  membershipYear?: number | "";
}) {
  return useQuery({
    queryKey: ["membership", "members", params],
    queryFn: () => membershipApi.listMembers(params),
  });
}

export function useMember(memberId?: number) {
  return useQuery({
    queryKey: ["membership", "member", memberId],
    queryFn: () => membershipApi.getMember(memberId as number),
    enabled: Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useMemberContacts(memberId?: number) {
  return useQuery({
    queryKey: ["membership", "member", memberId, "contacts"],
    queryFn: () => membershipApi.listContacts(memberId as number),
    enabled: Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useMemberFees(memberId?: number) {
  return useQuery({
    queryKey: ["membership", "member", memberId, "fees"],
    queryFn: () => membershipApi.listFees(memberId as number),
    enabled: Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useMemberDocuments(memberId?: number) {
  return useQuery({
    queryKey: ["membership", "member", memberId, "documents"],
    queryFn: () => membershipApi.listDocuments(memberId as number),
    enabled: Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useMemberIctAccess(memberId?: number, enabled = true) {
  return useQuery({
    queryKey: ["membership", "member", memberId, "ict"],
    queryFn: () => membershipApi.getIctAccess(memberId as number),
    enabled: enabled && Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useTradeFollowUps(memberId?: number) {
  return useQuery({
    queryKey: ["membership", "member", memberId, "trade-follow-ups"],
    queryFn: () => membershipApi.listTradeFollowUps(memberId as number),
    enabled: Boolean(memberId && Number.isFinite(memberId)),
  });
}

export function useCategoryPayments(financialYear?: string) {
  return useQuery({
    queryKey: ["membership", "reports", "category-payments", financialYear],
    queryFn: () => membershipApi.getCategoryPayments({ financialYear }),
    staleTime: 1000 * 60 * 5,
  });
}

/** @deprecated use useReportMemberPayments */
export function useMemberPayments(params: {
  financialYear?: string;
  categoryId?: number | "";
  paymentStatus?: PaymentStatus | "";
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  size?: number;
}) {
  return useQuery({
    queryKey: ["membership", "reports", "member-payments", params],
    queryFn: () => membershipApi.listMemberPayments(params),
  });
}

export function useYearlySummary() {
  return useQuery({
    queryKey: ["membership", "reports", "yearly-summary"],
    queryFn: membershipApi.getYearlySummary,
    staleTime: 1000 * 60 * 10,
  });
}

// ─── New report hooks (contract v2) ──────────────────────────────────────────

/**
 * Per-member payment rows for the reports page.
 * Returns a flat array (not paginated); filter client-side by category/paymentStatus/search.
 */
export function useReportMemberPayments(params: {
  financialYear?: string;
  status?: ReportMemberStatus | "";
}) {
  return useQuery({
    queryKey: ["membership", "reports", "member-payments-v2", params],
    queryFn: () => membershipApi.getReportMemberPayments(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Board-approval cohort sizes and fee totals per membership year.
 */
export function useReportYearlySummary() {
  return useQuery({
    queryKey: ["membership", "reports", "yearly-summary-v2"],
    queryFn: membershipApi.getYearlySummary,
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Category payment summary for a given financial year.
 */
export function useReportCategoryPayments(financialYear?: string) {
  return useQuery({
    queryKey: ["membership", "reports", "category-payments-v2", financialYear],
    queryFn: () => membershipApi.getCategoryPayments({ financialYear }),
    staleTime: 1000 * 60 * 5,
    enabled: !!financialYear,
  });
}

export function useMembershipMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["membership"] });
    },
  });
}